import { Space, Tag, Tooltip, Button } from 'antd'
import { EyeOutlined, SendOutlined } from '@ant-design/icons'
import type { ColumnType } from 'antd/es/table'
import type { TFunction } from 'i18next'

import type { FulfillmentOrderSummaryRoot, FulfillmentOrderDisplayStatusRoot } from '@repo/sdk'

import { StatusBadge, type StatusConfig } from '../../components/StatusBadge'
import { BuyerLink } from '../../components/BuyerLink'
import { formatKrw } from '@repo/shared'
import { FoAnnotationBadge } from './FoAnnotationBadge'
import { formatFoContext } from './orderDisplay'
import { formatKstDateTime } from '../../utils/formatKstDateTime'
import { getFulfillmentOrderPaidAmount } from '../../lib/orderShippingAmounts'

export type FoStatusConfigMap = Record<FulfillmentOrderDisplayStatusRoot, StatusConfig>

export interface OrderListColumnProps {
  onViewDetail: (id: string) => void
  t: TFunction
  statusConfig: FoStatusConfigMap
}

function getTotalAmount(record: FulfillmentOrderSummaryRoot): number {
  return getFulfillmentOrderPaidAmount(record)
}

export function createOrderListColumns({
  onViewDetail,
  t,
  statusConfig,
}: OrderListColumnProps): ColumnType<FulfillmentOrderSummaryRoot>[] {
  return [
    {
      title: t('common.columns.order'),
      dataIndex: 'fulfillmentOrderId',
      key: 'fulfillmentOrderId',
      width: 260,
      render: (_: unknown, record: FulfillmentOrderSummaryRoot) => (
        <Button
          data-testid={`order-list-id-link-${record.fulfillmentOrderId}`}
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => onViewDetail(record.fulfillmentOrderId)}
        >
          {formatFoContext(record, t)}
        </Button>
      ),
    },
    {
      title: t('common.columns.buyer'),
      key: 'buyer',
      width: 150,
      render: (_: unknown, record: FulfillmentOrderSummaryRoot) => (
        <BuyerLink buyer={record.buyer} showAvatar />
      ),
    },
    {
      title: t('common.columns.orderComposition'),
      key: 'orderComposition',
      width: 200,
      render: (_: unknown, record: FulfillmentOrderSummaryRoot) => (
        <Space size={4} wrap>
          <Tag>{t('pages.orders.compositionTotal', { count: record.summary.total })}</Tag>
          <Tag color="blue">
            {t('pages.orders.compositionActive', { count: record.summary.active })}
          </Tag>
          {record.summary.cancelled > 0 && (
            <Tag color="red">
              {t('pages.orders.compositionCancelled', { count: record.summary.cancelled })}
            </Tag>
          )}
          {record.summary.returned > 0 && (
            <Tag color="volcano">
              {t('pages.orders.compositionReturned', { count: record.summary.returned })}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('common.columns.amount'),
      key: 'amount',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: FulfillmentOrderSummaryRoot) =>
        formatKrw(getTotalAmount(record)),
    },
    {
      title: t('common.columns.status'),
      dataIndex: 'displayStatus',
      key: 'displayStatus',
      width: 160,
      render: (_: unknown, record: FulfillmentOrderSummaryRoot) => (
        <Space size={4} wrap>
          <StatusBadge status={record.displayStatus} configMap={statusConfig} />
          <FoAnnotationBadge annotation={record.annotation} />
        </Space>
      ),
    },
    {
      title: t('common.columns.createdDate'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (value: string) => (value ? formatKstDateTime(value, 'YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: t('common.columns.actions'),
      key: 'actions',
      width: 120,
      render: (_: unknown, record: FulfillmentOrderSummaryRoot) => (
        <Space>
          <Tooltip title={t('common.buttons.view')}>
            <Button
              data-testid={`order-list-view-btn-${record.fulfillmentOrderId}`}
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onViewDetail(record.fulfillmentOrderId)}
            />
          </Tooltip>
          {record.displayStatus === 'PAID' && (
            <Tooltip title={t('common.buttons.processShipping')}>
              <Button
                data-testid={`order-list-ship-btn-${record.fulfillmentOrderId}`}
                type="text"
                size="small"
                icon={<SendOutlined />}
                onClick={() => onViewDetail(record.fulfillmentOrderId)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ]
}
